// src/components/KeyValuesDashboard/components/KeyValueTabs.tsx

import { useState } from 'react';
import {
  BarChart3,
  Globe,
  Monitor,
  Calendar,
  Lightbulb,
} from 'lucide-react';

import type { ComparisonResponse } from '@/types/analytics';

// Tab Components
import { OverviewTab } from './tabs/OverviewTab';
import { DailyTab } from './tabs/DailyTab';
import { CountryTab } from './tabs/CountryTab';
import { AdUnitTab } from './tabs/AdUnitTab';
import { InsightsTab } from './tabs/InsightsTab';

interface KeyValueTabsProps {
  data: ComparisonResponse;
  mode: 'all' | 'compare';
  selectedKey: string;
  isLoading: boolean;
}

const tabs = [
  { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
  { id: 'daily', label: 'Por Dia', icon: Calendar },
  { id: 'country', label: 'Por País', icon: Globe },
  { id: 'adunit', label: 'Por Ad Unit', icon: Monitor },
  { id: 'insights', label: 'Insights', icon: Lightbulb },
];

export function KeyValueTabs({ data, mode, selectedKey }: KeyValueTabsProps) {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      
      {/* Tabs Navigation */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-all relative ${
                  isActive
                    ? 'text-indigo-600 bg-gradient-to-br from-indigo-50 to-purple-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-slate-400'}`} />
                {tab.label}
                
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-600" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'overview' && (
          <OverviewTab data={data} mode={mode} selectedKey={selectedKey} />
        )}
        
        {activeTab === 'daily' && (
          <DailyTab data={data} mode={mode} />
        )}
        
        {activeTab === 'country' && (
          <CountryTab data={data} mode={mode} />
        )}
        
        {activeTab === 'adunit' && (
          <AdUnitTab data={data} mode={mode} />
        )}
        
        {activeTab === 'insights' && (
          <InsightsTab data={data} mode={mode} />
        )}
      </div>
    </div>
  );
}
